import { useState } from 'react'
import { X, Download, Loader2 } from 'lucide-react'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import type { DashboardReport, DashboardMode, VideoMetrics } from '../../../types/dashboard'

// ─── Adzymic brand palette ────────────────────────────────────────────────────
type RGB = [number, number, number]

const B: Record<string, RGB> = {
  pearl:      [0,   28,  28],   // #001C1C  Black Pearl — dark header bg
  teal:       [0,   83,  86],   // #005356  Deep Teal   — section headers
  cyan:       [4,  132, 132],   // #048484  Dark Cyan   — accents
  turquoise:  [88, 128, 128],   // #588080  Grey Turquoise — muted text
  green:      [147,200,  84],   // #93C854  Adzymic Green — primary accent
  greenLight: [228,243, 178],   // tint of green for alt rows
  greenPale:  [243,250, 234],   // #f3faea  primary-50
  dark:       [26,  26,  26],   // #1A1A1A  Dark Grey — body text
  lightGrey:  [226,226, 226],   // #E2E2E2  separators
  offWhite:   [248,250, 248],   // very light bg
  white:      [255,255, 255],
}

// ─── page / layout constants ─────────────────────────────────────────────────
const PW     = 297   // A4 landscape width mm
const PH     = 210   // A4 landscape height mm
const MARGIN = 12
const CW     = PW - MARGIN * 2   // 273 mm usable width

// ─── video metric helpers ────────────────────────────────────────────────────
const VIDEO_ORDER  = ['5s_viewed','25_viewed','50_viewed','75_viewed','100_viewed']
const VIDEO_LABELS: Record<string, string> = {
  '5s_viewed': '5s viewed', '25_viewed': '25% viewed',
  '50_viewed': '50% viewed','75_viewed': '75% viewed','100_viewed': '100% viewed',
}

const fmt = (n: number) => n.toLocaleString()
const pct = (n: number) => `${n.toFixed(2)}%`

function normalizeVm(vm: VideoMetrics): VideoMetrics {
  const result: VideoMetrics = {}
  for (const [k, v] of Object.entries(vm)) {
    result[k.replace('%', '').replace(/_view$/, '_viewed')] = v
  }
  return result
}

function activeVideoCols(vms: VideoMetrics[]): string[] {
  return VIDEO_ORDER.filter(k =>
    vms.some(vm => { const n = normalizeVm(vm); return n[k] !== undefined && n[k].count > 0 })
  )
}

function videoCell(vm: VideoMetrics, key: string): string {
  const m = normalizeVm(vm)[key]
  return m && m.count > 0 ? `${fmt(m.count)} (${pct(m.pct)})` : '—'
}

// ─── drawing helpers ─────────────────────────────────────────────────────────
const sf = (doc: jsPDF, c: RGB) => doc.setFillColor(c[0], c[1], c[2])
const st = (doc: jsPDF, c: RGB) => doc.setTextColor(c[0], c[1], c[2])
const sd = (doc: jsPDF, c: RGB) => doc.setDrawColor(c[0], c[1], c[2])

function hline(doc: jsPDF, y: number, color: RGB, w = 0.3) {
  sd(doc, color); doc.setLineWidth(w)
  doc.line(MARGIN, y, PW - MARGIN, y)
}

// ─── footer ──────────────────────────────────────────────────────────────────
function footer(doc: jsPDF, page: number, total: number, name: string) {
  const y = PH - 10
  hline(doc, y, B.green, 0.5)
  doc.setFontSize(7); doc.setFont('helvetica','normal')
  st(doc, B.turquoise)
  doc.text(`Page ${page} / ${total}`, MARGIN, y + 4)
  doc.text(
    `Generated ${new Date().toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'})}`,
    PW / 2, y + 4, { align: 'center' }
  )
  doc.text(name, PW - MARGIN, y + 4, { align: 'right' })
}

// ─── header block ─────────────────────────────────────────────────────────────
function drawHeader(doc: jsPDF, report: DashboardReport): number {
  const hasAdUnit = !!report.ad_unit?.name
  const H = hasAdUnit ? 34 : 28

  // Background: Black Pearl
  sf(doc, B.pearl); doc.rect(0, 0, PW, H, 'F')

  // Subtle teal gradient band on the right
  sf(doc, B.teal)
  doc.rect(PW * 0.65, 0, PW * 0.35, H, 'F')

  // Diagonal cutoff to make the teal band look slanted
  sf(doc, B.pearl)
  doc.triangle(PW * 0.6, 0, PW * 0.72, 0, PW * 0.6, H, 'F')

  // Top green accent bar
  sf(doc, B.green); doc.rect(0, 0, PW, 3, 'F')

  // Campaign name
  const campName = report.campaign?.name || 'Campaign'
  const maxW = PW * 0.58 - MARGIN
  doc.setFontSize(13); doc.setFont('helvetica', 'bold')
  st(doc, B.white)
  doc.text(campName, MARGIN, 14, { maxWidth: maxW })

  // Ad unit name
  let metaY = 21
  if (hasAdUnit) {
    doc.setFontSize(8.5); doc.setFont('helvetica', 'normal')
    st(doc, B.greenLight)
    doc.text(`Ad Unit: ${report.ad_unit!.name}`, MARGIN, 22, { maxWidth: maxW })
    metaY = 29
  }

  // Meta row
  doc.setFontSize(7); doc.setFont('helvetica', 'normal')
  st(doc, B.turquoise)
  doc.text(
    `${report.date_range.from}  →  ${report.date_range.to}`,
    MARGIN, metaY
  )

  // Bottom green line
  sf(doc, B.green); doc.rect(0, H, PW, 1.5, 'F')

  return H + 1.5
}

// ─── stat cards row ───────────────────────────────────────────────────────────
const STAT_ACCENTS: RGB[] = [B.green, B.cyan, B.teal, B.cyan, B.turquoise]

function drawStatCards(doc: jsPDF, report: DashboardReport, startY: number): number {
  const ov = report.overview
  const stats = [
    { label: 'Impressions',     value: fmt(ov.impressions) },
    { label: 'Clicks',          value: fmt(ov.clicks) },
    { label: 'CTR',             value: pct(ov.ctr) },
    { label: 'Engagement',      value: fmt(ov.engagement) },
    { label: 'Engagement Rate', value: pct(ov.engagement_rate) },
  ]
  const gap = 3, cw = (CW - gap * 4) / 5, ch = 22, y = startY

  stats.forEach((s, i) => {
    const x = MARGIN + i * (cw + gap)
    const a = STAT_ACCENTS[i]
    // Card
    sf(doc, B.white); sd(doc, B.lightGrey); doc.setLineWidth(0.2)
    doc.rect(x, y, cw, ch, 'FD')
    // Top accent stripe
    sf(doc, a); doc.rect(x, y, cw, 2.5, 'F')
    // Value
    doc.setFontSize(11); doc.setFont('helvetica', 'bold')
    st(doc, a); doc.text(s.value, x + cw / 2, y + 13, { align: 'center' })
    // Label
    doc.setFontSize(6); doc.setFont('helvetica', 'normal')
    st(doc, B.turquoise); doc.text(s.label, x + cw / 2, y + 19.5, { align: 'center' })
  })

  let ny = y + ch + 4

  // Video metrics tiles
  const normOvVm = normalizeVm(ov.video_metrics)
  const vk = VIDEO_ORDER.filter(k => normOvVm[k] !== undefined && normOvVm[k].count > 0)
  if (vk.length > 0) {
    const vw = (CW - gap * (vk.length - 1)) / vk.length, vh = 14
    vk.forEach((k, i) => {
      const m = normOvVm[k]
      const x = MARGIN + i * (vw + gap)
      sf(doc, B.greenPale); sd(doc, B.green); doc.setLineWidth(0.3)
      doc.rect(x, ny, vw, vh, 'FD')
      doc.setFontSize(9); doc.setFont('helvetica', 'bold')
      st(doc, B.teal); doc.text(fmt(m.count), x + vw / 2, ny + 7, { align: 'center' })
      doc.setFontSize(6); doc.setFont('helvetica', 'normal')
      st(doc, B.turquoise); doc.text(`${VIDEO_LABELS[k]}  (${pct(m.pct)})`, x + vw / 2, ny + 11.5, { align: 'center' })
    })
    ny += vh + 4
  }

  return ny
}

// ─── section heading bar ─────────────────────────────────────────────────────
function sectionHeading(doc: jsPDF, title: string, y: number): number {
  // Full-width teal bar
  sf(doc, B.teal); doc.rect(0, y, PW, 8, 'F')
  // Green left accent
  sf(doc, B.green); doc.rect(0, y, 3, 8, 'F')
  doc.setFontSize(8.5); doc.setFont('helvetica', 'bold')
  st(doc, B.white)
  doc.text(title.toUpperCase(), MARGIN + 4, y + 5.5)
  return y + 8
}

// ─── autoTable wrapper ────────────────────────────────────────────────────────
function addTable(
  doc: jsPDF, title: string,
  head: string[][], body: any[][],
  startY: number, campName: string
): number {
  const tableY = sectionHeading(doc, title, startY) + 2

  autoTable(doc, {
    startY: tableY,
    head,
    body,
    theme: 'plain',
    headStyles: {
      fillColor: B.pearl,
      textColor: B.white,
      fontStyle: 'bold',
      fontSize: 8,
      cellPadding: { top: 3, right: 4, bottom: 3, left: 4 },
    },
    bodyStyles: {
      fontSize: 8,
      textColor: B.dark,
      cellPadding: { top: 2.5, right: 4, bottom: 2.5, left: 4 },
    },
    alternateRowStyles: { fillColor: B.greenPale },
    styles: { lineColor: B.lightGrey, lineWidth: 0.15 },
    margin: { left: MARGIN, right: MARGIN, bottom: 20 },
    didDrawPage(pageData) {
      // Re-draw the green top bar so it stays on every page
      sf(doc, B.green); doc.rect(0, 0, PW, 3, 'F')
      footer(doc, pageData.pageNumber, (doc as any).internal.getNumberOfPages(), campName)
    },
  })

  return (doc as any).lastAutoTable.finalY + 8
}

// ─── PDF generator ─────────────────────────────────────────────────────────────
async function generatePdf(
  report: DashboardReport,
  mode: DashboardMode,
  selected: Set<string>,
  filename: string
) {
  const doc      = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' })
  const campName = report.campaign?.name || 'Campaign'

  const headerH = drawHeader(doc, report)
  ;(doc as any).lastAutoTable = { finalY: headerH }
  let y = headerH + 6

  // ── Overview ──
  if (selected.has('overview')) {
    y = drawStatCards(doc, report, y) + 6
  }

  // ── Ad Units (campaign mode only) ──
  if (selected.has('ad-units') && mode === 'campaign') {
    const vCols = activeVideoCols(report.performance.map(r => r.video_metrics))
    const head = [['Name', 'Type', 'Sizes', 'Impressions', 'Clicks', 'CTR %', 'Eng. Rate %', 'Engagement', ...vCols.map(k => VIDEO_LABELS[k])]]
    const rows = report.performance.map(r => [
      r.name ?? '',
      r.type ?? '',
      r.sizes?.join(', ') ?? '',
      fmt(r.impressions), fmt(r.clicks), pct(r.ctr), pct(r.engagement_rate), fmt(r.engagement),
      ...vCols.map(k => videoCell(r.video_metrics, k)),
    ])
    y = addTable(doc, 'Ad Units', head, rows, y, campName)
  }

  // ── Performance ──
  if (selected.has('performance')) {
    if (mode === 'campaign') {
      const vCols = activeVideoCols(report.performance.map(r => r.video_metrics))
      const head = [['Id', 'Name', 'Type', 'Impressions', 'Clicks', 'CTR %', 'Eng. Rate %', 'Engagement', ...vCols.map(k => VIDEO_LABELS[k])]]
      const rows = report.performance.map(r => [
        r.id, r.name, r.type,
        fmt(r.impressions), fmt(r.clicks), pct(r.ctr), pct(r.engagement_rate), fmt(r.engagement),
        ...vCols.map(k => videoCell(r.video_metrics, k)),
      ])
      y = addTable(doc, 'Performance', head, rows, y, campName)
    } else {
      const vCols = activeVideoCols(report.performance.map(r => r.video_metrics))
      const head = [['Size', 'Content Type', 'Impressions', 'Clicks', 'CTR %', 'Eng. Rate %', 'Engagement', ...vCols.map(k => VIDEO_LABELS[k])]]
      const rows = report.performance.map(r => [
        r.size, r.content_type,
        fmt(r.impressions), fmt(r.clicks), pct(r.ctr), pct(r.engagement_rate), fmt(r.engagement),
        ...vCols.map(k => videoCell(r.video_metrics, k)),
      ])
      y = addTable(doc, 'Content Type and Size', head, rows, y, campName)
    }
  }

  // ── Content Types ──
  if (selected.has('content-types')) {
    const vCols = activeVideoCols(report.content_types.map(r => r.video_metrics))
    const head = [['Content Type', 'CTR %', 'Impressions', 'Clicks', 'Engagement', ...vCols.map(k => VIDEO_LABELS[k])]]
    const rows = report.content_types.map(r => [
      r.content_type, pct(r.ctr), fmt(r.impressions), fmt(r.clicks), fmt(r.engagement),
      ...vCols.map(k => videoCell(r.video_metrics, k)),
    ])
    y = addTable(doc, 'Content Types', head, rows, y, campName)
  }

  // ── Ad Sizes ──
  if (selected.has('ad-sizes')) {
    const vCols = activeVideoCols(report.ad_sizes.map(r => r.video_metrics))
    const head = [['Size', 'CTR %', 'Impressions', 'Clicks', 'Engagement', ...vCols.map(k => VIDEO_LABELS[k])]]
    const rows = report.ad_sizes.map(r => [
      r.size, pct(r.ctr), fmt(r.impressions), fmt(r.clicks), fmt(r.engagement),
      ...vCols.map(k => videoCell(r.video_metrics, k)),
    ])
    y = addTable(doc, 'Ad Sizes', head, rows, y, campName)
  }

  // ── Top 20 Domains ──
  if (selected.has('top-domains')) {
    const vCols = activeVideoCols(report.top_domains.map(r => r.video_metrics))
    const head = [['Domain', 'Impressions', 'Clicks', 'CTR %', 'Eng. Rate %', 'Engagement', ...vCols.map(k => VIDEO_LABELS[k])]]
    const rows = report.top_domains.map(r => [
      r.domain,
      fmt(r.impressions), fmt(r.clicks), pct(r.ctr), pct(r.engagement_rate), fmt(r.engagement),
      ...vCols.map(k => videoCell(r.video_metrics, k)),
    ])
    y = addTable(doc, 'Top 20 Domains', head, rows, y, campName)
  }

  // ── Footers on every page ──
  const total = (doc as any).internal.getNumberOfPages()
  for (let i = 1; i <= total; i++) {
    doc.setPage(i)
    footer(doc, i, total, campName)
  }

  doc.save(`${filename}.pdf`)
}

// ─── modal ────────────────────────────────────────────────────────────────────
const SECTIONS = [
  { id: 'overview',      label: 'Overview',          modes: ['campaign', 'ad_unit'] },
  { id: 'ad-units',      label: 'Ad Units',           modes: ['campaign'] },
  { id: 'performance',   label: 'Performance',        modes: ['campaign', 'ad_unit'] },
  { id: 'content-types', label: 'Content Types',      modes: ['campaign', 'ad_unit'] },
  { id: 'ad-sizes',      label: 'Ad Sizes',           modes: ['campaign', 'ad_unit'] },
  { id: 'top-domains',   label: 'Top 20 Domains',     modes: ['campaign', 'ad_unit'] },
]

interface Props {
  isOpen: boolean
  onClose: () => void
  report: DashboardReport
  mode: DashboardMode
  filename: string
}

export function PdfDownloadModal({ isOpen, onClose, report, mode, filename }: Props) {
  const availableSections = SECTIONS.filter(s => s.modes.includes(mode))
  const [selected, setSelected] = useState<Set<string>>(new Set(availableSections.map(s => s.id)))
  const [isGenerating, setIsGenerating] = useState(false)

  if (!isOpen) return null

  const toggle = (id: string) =>
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })

  const handleDownload = async () => {
    setIsGenerating(true)
    try {
      await generatePdf(report, mode, selected, filename)
    } finally {
      setIsGenerating(false)
      onClose()
    }
  }

  const sections = availableSections.map(s =>
    s.id === 'performance' && mode === 'ad_unit' ? { ...s, label: 'Content Type and Size' } : s
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Download PDF</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500">
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Select sections to include:</p>

        <div className="space-y-3 mb-6">
          {sections.map(s => (
            <label key={s.id} className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={selected.has(s.id)}
                onChange={() => toggle(s.id)}
                className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">{s.label}</span>
            </label>
          ))}
        </div>

        <button
          onClick={handleDownload}
          disabled={selected.size === 0 || isGenerating}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 dark:disabled:bg-gray-700 text-white rounded-lg text-sm font-medium transition-colors"
        >
          {isGenerating ? (
            <><Loader2 className="h-4 w-4 animate-spin" /> Generating…</>
          ) : (
            <><Download className="h-4 w-4" /> Download PDF</>
          )}
        </button>
      </div>
    </div>
  )
}
